import { useEffect } from 'react';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useTenantStore } from '@core/tenant/useTenantStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import type { User } from '@core/generated/gql-types';
import { useTenantId } from '@/contexts/TenantContext';
import type { WorkersListView } from '@/modules/workers/views/workersList.view';

type UseWorkersListData = {
  workers: User[];
  isLoading: boolean;
  isFetchingMoreWorkers: boolean;
  tenantId: string | null;
};

export const useWorkersListData = (
  view: WorkersListView,
): UseWorkersListData => {
  const tenantId = useTenantId();
  const {
    workers,
    fetchWorkersForTenant,
    cancelWorkersFetchForTenant,
    isLoading,
    isFetchingMoreWorkers,
  } = useWorkerStore();
  const { fetchTenantUsersForTenant } = useTenantStore();
  const { fetchClassificationGroups } = useClassificationStore();

  useEffect(() => {
    if (!tenantId) return;

    if (view.query.classifications.fetch) {
      fetchClassificationGroups(tenantId);
    }

    const didStartTenantWorkersFetch = view.query.workers.strategy === 'tenant';

    if (didStartTenantWorkersFetch) {
      void fetchWorkersForTenant(tenantId);
    }

    if (view.query.tenantUsers.fetch) {
      fetchTenantUsersForTenant(tenantId);
    }

    return () => {
      if (didStartTenantWorkersFetch) {
        cancelWorkersFetchForTenant();
      }
    };
  }, [
    tenantId,
    view.query.classifications.fetch,
    view.query.workers.strategy,
    view.query.tenantUsers.fetch,
    fetchClassificationGroups,
    fetchWorkersForTenant,
    cancelWorkersFetchForTenant,
    fetchTenantUsersForTenant,
  ]);

  return {
    workers,
    isLoading,
    isFetchingMoreWorkers,
    tenantId,
  };
};
